// File: frontend/src/hooks/useTeamEntries.ts
// src/hooks/useTeamEntries.ts

import { useState, useCallback, useEffect, useRef } from "react";
import { EntryModel } from "../models";
import { fetchEntriesByTeamId } from "../services/clubEntryService";
import { ENTRIES_PER_PAGE } from "../constants";
import { useAuth } from "../context/AuthContext";

/**
 * initialPage — the page to load, taken from the "?sayfa=" URL parameter by the
 * page component (see useUrlPage). Kept in the URL rather than in state so a
 * page can be linked to, survives a refresh, and gives search engines one
 * address per page instead of one per topic.
 *
 * Changing any filter resets to page 1 on purpose: page 7 of an unfiltered list
 * has nothing to do with page 7 of a filtered one, and landing on an empty page
 * would look like a fault. The page component clears the URL parameter to match.
 */
export const useTeamEntries = (
  teamId: number | null,
  showRookieEntries: boolean = true,
  sort: string = "newest",
  entriesPerPage: number = ENTRIES_PER_PAGE,
  filterToday: boolean = false,
  rookieOnly: boolean = false,
  keyword: string = "",
  initialPage: number = 1
) => {
  const { user } = useAuth();

  const [entries, setEntries]         = useState<EntryModel[]>([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalItems, setTotalItems]   = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Ticket 1c: sequence number of the most recently STARTED entry request.
  //
  // Every fetch takes a copy of this counter before it awaits and compares it
  // again afterwards. If the counter moved on in the meantime a newer request
  // has been fired — normally because the reader clicked another team in the
  // left pane — and this response is stale, so it must not be written to state.
  //
  // Without this guard the responses are applied in arrival order rather than
  // request order: a slow response for the previously open team landing after
  // the fast response for the current one overwrites the list, and the page
  // ends up showing one team's header above another team's entries.
  const requestIdRef = useRef(0);

  // Mirrors initialPage so the effect below can read it without adding a
  // dependency that would refetch on every unrelated render.
  const initialPageRef = useRef(initialPage);
  initialPageRef.current = initialPage;

  // Stable refs so callbacks don't need teamId in their dep arrays
  const teamIdRef           = useRef(teamId);
  const showRookieRef       = useRef(showRookieEntries);
  const sortRef             = useRef(sort);
  const entriesPerPageRef   = useRef(entriesPerPage);
  teamIdRef.current         = teamId;
  showRookieRef.current     = showRookieEntries;
  sortRef.current           = sort;
  entriesPerPageRef.current = entriesPerPage;
  // Toolbar filter refs — keep values fresh inside stable callbacks
  const filterTodayRef      = useRef(filterToday);
  const rookieOnlyRef       = useRef(rookieOnly);
  const keywordRef          = useRef(keyword);
  filterTodayRef.current    = filterToday;
  rookieOnlyRef.current     = rookieOnly;
  keywordRef.current        = keyword;

  const loadEntries = useCallback(async (page: number = 1) => {
    // Ticket 1c: claim a sequence number for this request before anything else.
    const requestId = ++requestIdRef.current;
    const id = teamIdRef.current;
    if (!id) { setEntries([]); setTotalItems(0); setTotalPages(1); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEntriesByTeamId(id, page, entriesPerPageRef.current, showRookieRef.current, sortRef.current, filterTodayRef.current, rookieOnlyRef.current, keywordRef.current);
      // Ticket 1c: a newer request was started while this one was in flight —
      // its result is the one the reader is waiting for, so drop this response.
      if (requestId !== requestIdRef.current) return;
      setEntries(res.entries);
      setCurrentPage(res.currentPage);
      setTotalPages(res.totalPages);
      setTotalItems(res.totalItems);
    } catch {
      // A stale failure must not surface either: the current team may well be
      // loading fine, and the error message would replace its entry list.
      if (requestId !== requestIdRef.current) return;
      setError("Tanımlar yüklenemedi.");
    } finally {
      // Only the newest request owns the loading flag. Clearing it from a stale
      // one would hide the spinner while the real request is still running.
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []); // stable — uses refs

  // Ticket 1c: drop the previous team's entries the moment the team changes.
  // The fetch below replaces them anyway, but only once it comes back; until
  // then the old list would still be rendered under the new team's header.
  // Declared before the loading effect so both run in the same commit and the
  // reader never sees an empty-state flash.
  useEffect(() => {
    setEntries([]);
    setTotalItems(0);
    setTotalPages(1);
    setCurrentPage(1);
  }, [teamId]);

  // Reload when teamId, auth, or filters change
  useEffect(() => {
    loadEntries(initialPageRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, user?.id, showRookieEntries, sort, filterToday, rookieOnly, keyword, initialPage]);

  const loadMoreEntries = useCallback(async () => {
    const id = teamIdRef.current;
    if (!id || isLoadingMore || currentPage >= totalPages) return;
    // Ticket 1c: "load more" appends, so a stale response is even worse here —
    // it would mix another team's entries into the current list. Remember which
    // request generation this belongs to and verify it after the round trip.
    const requestId = requestIdRef.current;
    setIsLoadingMore(true);
    try {
      const res = await fetchEntriesByTeamId(id, currentPage + 1, entriesPerPageRef.current, showRookieRef.current, sortRef.current, filterTodayRef.current, rookieOnlyRef.current, keywordRef.current);
      if (requestId !== requestIdRef.current) return;
      setEntries(prev => [...prev, ...res.entries]);
      setCurrentPage(res.currentPage);
      setTotalPages(res.totalPages);
    } catch {
      // Silent failure
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, currentPage, totalPages]);

  const updateEntries = useCallback(
    (callback: (prev: EntryModel[]) => EntryModel[]) => setEntries(callback),
    []
  );

  return {
    entries, loading, error,
    currentPage, totalPages, totalItems,
    isLoadingMore,
    loadEntries, loadMoreEntries, updateEntries,
  };
};